/** Shared Rich Text limits and safety checks for homepage CMS content. */

export const RICH_TEXT_MAX_PLAIN_CHARS = 5_000;
export const RICH_TEXT_MAX_HTML_CHARS = 20_000;

const DANGEROUS_HTML_TAGS =
  "script|style|iframe|object|embed|form|link|meta|base|svg|math";

const UNSAFE_RICH_TEXT = new RegExp(
  `<(${DANGEROUS_HTML_TAGS})\\b|<\\/(?:${DANGEROUS_HTML_TAGS})>|\\son\\w+\\s*=|javascript\\s*:|data\\s*:\\s*text\\/html|vbscript\\s*:|expression\\s*\\(`,
  "i",
);

export function stripRichTextTags(html: string): string {
  return html
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .trim();
}

export function looksLikeHtml(value: string): boolean {
  return /<\/?[a-z][\s\S]*>/i.test(value);
}

export type RichTextValidationIssue = "tooLongPlain" | "tooLongHtml" | "unsafe";

export function validateRichTextContent(
  value: unknown,
): RichTextValidationIssue | null {
  if (value == null || value === "") return null;
  if (typeof value !== "string") return "unsafe";
  if (value.length > RICH_TEXT_MAX_HTML_CHARS) return "tooLongHtml";
  if (stripRichTextTags(value).length > RICH_TEXT_MAX_PLAIN_CHARS) {
    return "tooLongPlain";
  }
  if (UNSAFE_RICH_TEXT.test(value)) return "unsafe";
  return null;
}

/** Sanitize HTML for safe public rendering (allows color/highlight styles). */
export function sanitizeRichTextHtml(html = ""): string {
  const dangerousTags = new RegExp(
    `<(${DANGEROUS_HTML_TAGS})\\b[^>]*>[\\s\\S]*?<\\/\\1>`,
    "gi",
  );
  const dangerousVoidTags = new RegExp(
    `<(${DANGEROUS_HTML_TAGS})\\b[^>]*\\/?>`,
    "gi",
  );
  return html
    .replace(dangerousTags, "")
    .replace(dangerousVoidTags, "")
    .replace(/\son\w+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, "")
    .replace(
      /\s(?:href|src|xlink:href)\s*=\s*(["'])\s*(?:javascript|vbscript|data\s*:\s*text\/html)[\s\S]*?\1/gi,
      "",
    )
    .replace(
      /style\s*=\s*(["'])([\s\S]*?)\1/gi,
      (_match, quote: string, style: string) => {
        const safe = style
          .split(";")
          .map((part) => part.trim())
          .filter((part) =>
            /^(?:color|background-color|background|font-family|font-size|font-weight|font-style|text-align|text-decoration)\s*:/i.test(
              part,
            ),
          )
          .join("; ");
        return safe ? `style=${quote}${safe}${quote}` : "";
      },
    );
}
